import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import dimensionalGateShowcaseArt from '../../assets/dimensional-gate-showcase-art.avif';
import ensnaredShowcaseArt from '../../assets/ensnared-showcase-art.png';
import freshmarkShowcaseArt from '../../assets/freshmark-sushi-system-art.avif';
import netflixShowcaseImage from '../../assets/netflix-showcase-art.avif';
import unwrapLoveShowcaseArt from '../../assets/unwrap-love-showcase-art.avif';
import { projectThemes } from '../../projectThemes';
import './ShowcaseSection.css';

gsap.registerPlugin(ScrollTrigger);

type ShowcaseSectionProps = {
  animateIn?: boolean;
  note?: string;
};

type ShowcaseProject = {
  id: string;
  displayTitle: string;
  title: string;
  description: string;
  media: string;
  fallbackImage: string;
  mediaType: 'video' | 'image';
  mediaAlt: string;
  projectLink: string;
  projectLinkLabel: string;
  techLabel?: string;
  accent: string;
  stageColor: string;
  inkColor?: string;
  fadedInk?: string;
  detailMuted?: string;
  detailColor?: string;
  linkRule?: string;
};

type ShowcaseStyle = React.CSSProperties & {
  '--showcase-accent'?: string;
  '--showcase-stage'?: string;
  '--showcase-ink'?: string;
  '--showcase-faded-ink'?: string;
  '--showcase-detail-muted'?: string;
  '--showcase-detail'?: string;
  '--showcase-link-rule'?: string;
};

type ShowcaseScopeStyle = React.CSSProperties & {
  '--showcase-scope-index': number;
};

const showcaseProjects: ShowcaseProject[] = [
  {
    id: 'freshmark',
    displayTitle: 'FreshMark',
    title: 'FreshMark: Sushi Label Management System',
    description:
      'A label-management workflow for sushi production teams, connecting product data, store operations, printing, and HQ tracking in one practical system.',
    media: freshmarkShowcaseArt,
    fallbackImage: freshmarkShowcaseArt,
    mediaType: 'image',
    mediaAlt: 'FreshMark sushi labelling system 3D illustration',
    projectLink: '/freshmark',
    projectLinkLabel: 'View project',
    techLabel: 'Full-stack development · React · Node.js · SwiftUI',
    ...projectThemes.freshmark,
  },
  {
    id: 'netflix-dashboard',
    displayTitle: 'Netflix Insights',
    title: 'Netflix Content Distribution and Trend Analysis Dashboard',
    description:
      'A Tableau analysis of content type, country distribution, ratings, genre patterns, and release trends using a Netflix Kaggle dataset.',
    media: netflixShowcaseImage,
    fallbackImage: netflixShowcaseImage,
    mediaType: 'image',
    mediaAlt: 'Netflix insight dashboard case study preview artwork',
    projectLink: '/netflix-dashboard',
    projectLinkLabel: 'View project',
    techLabel: 'Data analysis · Tableau · Data storytelling',
    ...projectThemes.netflix,
  },
  {
    id: 'unwrap-love',
    displayTitle: 'Unwrap Love',
    title: 'Unwrap Love: An Experience Concept for Everyday Connection',
    description:
      'A visual concept for turning small acts of care into moments people can discover, share, and remember.',
    media: unwrapLoveShowcaseArt,
    fallbackImage: unwrapLoveShowcaseArt,
    mediaType: 'image',
    mediaAlt: 'Unwrap Love experience concept illustration',
    projectLink: '/unwrap-love',
    projectLinkLabel: 'View project',
    techLabel: 'Product concept · Experience design · Visual storytelling',
    ...projectThemes.unwrapLove,
  },
  {
    id: 'dimensional-gate',
    displayTitle: 'Dimensional Gate',
    title: 'Tmall IP Marketing Campaign for Gen-Z Engagement',
    description:
      'A campaign concept built around “The Dimensional Gate,” later adopted for the brand film and offline activation materials.',
    media: dimensionalGateShowcaseArt,
    fallbackImage: dimensionalGateShowcaseArt,
    mediaType: 'image',
    mediaAlt: 'Dimensional Gate luminous portal artwork',
    projectLink: '/dimensional-gate',
    projectLinkLabel: 'View project',
    techLabel: 'Advertising creative · Brand campaign · IP marketing',
    ...projectThemes.dimensionalGate,
  },
  {
    id: 'ensnared',
    displayTitle: 'Ensnared',
    title: 'Ensnared: A Board Game Built in 48 Hours at KiwiJam',
    description:
      'A physical board game about escaping a spider’s web, made at KiwiJam Auckland 2026 — custom item cards, 3D-printed pieces, and a winner of The Most Complete award.',
    media: ensnaredShowcaseArt,
    fallbackImage: ensnaredShowcaseArt,
    mediaType: 'image',
    mediaAlt: 'Ensnared board game spider web line artwork',
    projectLink: '/ensnared',
    projectLinkLabel: 'View project',
    techLabel: 'Board game design · Card design · 3D printing',
    ...projectThemes.ensnared,
  },
];

const SHOWCASE_HANDOFF_FALLBACK = '#d6d4d2';

const getReducedMotionPreference = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getProjectTriggerPercent = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 809px)').matches
    ? 10
    : 20;

export const resolveShowcaseProjectIndex = (
  scrollProjectIndex: number | null,
  hoverProjectIndex: number | null
) => hoverProjectIndex ?? scrollProjectIndex;

export const resolveShowcaseViewportProjectIndex = (
  stepTops: Array<number | null>,
  triggerLine: number
) =>
  stepTops.reduce<number | null>((latestIndex, stepTop, index) => {
    if (stepTop === null || stepTop > triggerLine) {
      return latestIndex;
    }

    return index;
  }, null);

export const setShowcasePageTheme = (isActive: boolean) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (isActive) {
    document.body.dataset.showcaseTheme = 'active';
    document.body.style.backgroundColor =
      document.body.style.getPropertyValue('--showcase-handoff-color') ||
      SHOWCASE_HANDOFF_FALLBACK;
    return;
  }

  delete document.body.dataset.showcaseTheme;
  document.body.style.removeProperty('background-color');
};

export const ShowcaseSection: React.FC<ShowcaseSectionProps> = ({
  note = 'My Showcase',
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<Array<HTMLLIElement | null>>([]);
  const refreshTimeoutRef = useRef<number | null>(null);
  const [scrollProjectIndex, setScrollProjectIndex] = useState<number | null>(null);
  const [hoverProjectIndex, setHoverProjectIndex] = useState<number | null>(null);
  const [isPreviewViewportActive, setIsPreviewViewportActive] = useState(false);

  const readViewportProjectIndex = () => {
    const triggerLine =
      window.innerHeight * (getProjectTriggerPercent() / 100);

    return resolveShowcaseViewportProjectIndex(
      stepRefs.current
        .slice(0, showcaseProjects.length)
        .map((step) => step?.getBoundingClientRect().top ?? null),
      triggerLine
    );
  };

  const restoreViewportProject = () => {
    setScrollProjectIndex(readViewportProjectIndex());
    setHoverProjectIndex(null);
  };

  useLayoutEffect(() => {
    if (!sectionRef.current || getReducedMotionPreference()) {
      return undefined;
    }

    const projectTriggerPercent = getProjectTriggerPercent();

    const triggers = stepRefs.current
      .slice(0, showcaseProjects.length)
      .map((step, index) => {
        if (!step) {
          return null;
        }

        return ScrollTrigger.create({
          trigger: step,
          start: `top ${projectTriggerPercent}%`,
          end: `bottom ${projectTriggerPercent}%`,
          onEnter: () => setScrollProjectIndex(index),
          onEnterBack: () => setScrollProjectIndex(index),
          invalidateOnRefresh: true,
        });
      })
      .filter(Boolean) as ScrollTrigger[];

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  useLayoutEffect(() => {
    if (!sectionRef.current) {
      return undefined;
    }

    const activateFromTop = () => {
      setShowcasePageTheme(true);
      setScrollProjectIndex(null);
    };

    const activateFromBottom = () => {
      setShowcasePageTheme(true);
      setScrollProjectIndex((currentIndex) =>
        currentIndex ?? showcaseProjects.length - 1
      );
    };

    const deactivateSection = () => {
      setShowcasePageTheme(false);
      setScrollProjectIndex(null);
      setHoverProjectIndex(null);
    };

    const themeTrigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top 20%',
      end: 'bottom top',
      invalidateOnRefresh: true,
      onEnter: activateFromTop,
      onEnterBack: activateFromBottom,
      onLeave: deactivateSection,
      onLeaveBack: deactivateSection,
      onRefresh: (instance) => {
        if (instance.isActive) {
          setShowcasePageTheme(true);
          setScrollProjectIndex(readViewportProjectIndex());
          return;
        }

        deactivateSection();
      },
    });

    return () => {
      themeTrigger.kill();
      setShowcasePageTheme(false);
    };
  }, []);

  useLayoutEffect(() => {
    if (!sectionRef.current) {
      return undefined;
    }

    const section = sectionRef.current;
    const syncPreviewViewportState = () => {
      const sectionBounds = section.getBoundingClientRect();
      const isActive =
        sectionBounds.top < window.innerHeight &&
        sectionBounds.bottom > window.innerHeight;

      setIsPreviewViewportActive((currentValue) =>
        currentValue === isActive ? currentValue : isActive
      );

      if (!isActive) {
        setHoverProjectIndex(null);
      }
    };

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(syncPreviewViewportState);

    syncPreviewViewportState();
    resizeObserver?.observe(section);
    window.addEventListener('scroll', syncPreviewViewportState, { passive: true });
    window.addEventListener('resize', syncPreviewViewportState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', syncPreviewViewportState);
      window.removeEventListener('resize', syncPreviewViewportState);
    };
  }, []);

  const activeProjectIndex = resolveShowcaseProjectIndex(
    scrollProjectIndex,
    hoverProjectIndex
  );
  const activeProject = activeProjectIndex === null
    ? null
    : showcaseProjects[activeProjectIndex];
  const isPreviewVisible = isPreviewViewportActive && activeProject !== null;
  const handoffColor = activeProject?.stageColor ?? SHOWCASE_HANDOFF_FALLBACK;

  useLayoutEffect(() => {
    document.body.style.setProperty('--showcase-handoff-color', handoffColor);

    if (document.body.dataset.showcaseTheme === 'active') {
      document.body.style.backgroundColor = handoffColor;
    }
  }, [handoffColor]);

  useLayoutEffect(() => {
    return () => {
      document.body.style.removeProperty('--showcase-handoff-color');

      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const handleStepBlur = (
    event: React.FocusEvent<HTMLLIElement>,
    projectIndex: number
  ) => {
    const nextTarget = event.relatedTarget;

    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }

    setHoverProjectIndex((currentIndex) =>
      currentIndex === projectIndex ? null : currentIndex
    );
    setScrollProjectIndex(readViewportProjectIndex());
  };

  const handleStepTransitionEnd = (
    event: React.TransitionEvent<HTMLLIElement>
  ) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === 'grid-template-rows'
    ) {
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = window.setTimeout(() => {
        ScrollTrigger.refresh();
        refreshTimeoutRef.current = null;
      }, 80);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="showcase-section"
      aria-labelledby="showcase-title"
      data-showcase-state={activeProject?.id ?? 'idle'}
      style={{
        '--showcase-accent': activeProject?.accent,
        '--showcase-stage': activeProject?.stageColor ?? 'transparent',
        '--showcase-ink': activeProject?.inkColor,
        '--showcase-faded-ink': activeProject?.fadedInk,
        '--showcase-detail-muted': activeProject?.detailMuted,
        '--showcase-detail': activeProject?.detailColor,
        '--showcase-link-rule': activeProject?.linkRule,
      } as ShowcaseStyle}
    >
      <div className="showcase-section__sticky">
        <header className="showcase-section__intro">
          <div className="showcase-section__intro-inner">
            <h2 id="showcase-title" className="showcase-section__title">
              {note}
            </h2>
          </div>
        </header>

        <div className="showcase-section__inner">
          <div className="showcase-section__desktop-layout">
            <ol
              className="showcase-section__steps"
              aria-label="Showcase projects"
              onMouseLeave={restoreViewportProject}
            >
              {showcaseProjects.map((project, index) => {
                const isActive = index === activeProjectIndex;

                return (
                  <li
                    key={project.id}
                    ref={(node) => {
                      stepRefs.current[index] = node;
                    }}
                    className={`showcase-section__step${isActive ? ' is-active' : ''}`}
                    onMouseEnter={() => setHoverProjectIndex(index)}
                    onFocusCapture={() => setHoverProjectIndex(index)}
                    onBlurCapture={(event) => handleStepBlur(event, index)}
                    onTransitionEnd={handleStepTransitionEnd}
                  >
                    <div className="showcase-section__step-heading">
                      <Link
                        className="showcase-section__step-title"
                        to={project.projectLink}
                        aria-label={project.title}
                      >
                        <span aria-hidden="true">{project.displayTitle}</span>
                      </Link>
                    </div>
                    <div
                      className={`showcase-section__active-detail${
                        isActive ? ' is-active' : ''
                      }`}
                      aria-hidden={!isActive}
                    >
                      <div className="showcase-section__active-detail-inner">
                        <p className="showcase-section__step-copy">
                          {project.description}
                        </p>
                        <div className="showcase-section__step-footer">
                          {project.techLabel ? (
                            <div
                              className="showcase-section__scope-list"
                              aria-label="Tools and disciplines"
                            >
                              {project.techLabel.split(' · ').map((label, scopeIndex) => (
                                <span
                                  key={label}
                                  style={{
                                    '--showcase-scope-index': scopeIndex,
                                  } as ShowcaseScopeStyle}
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <Link
                            className="showcase-section__project-link"
                            to={project.projectLink}
                            tabIndex={isActive ? 0 : -1}
                          >
                            {project.projectLinkLabel}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="showcase-section__mobile-list" aria-label="Showcase project cards">
            {showcaseProjects.map((project) => (
              <article
                key={project.id}
                className="showcase-section__mobile-card"
                data-project-id={project.id}
                style={{
                  '--showcase-accent': project.accent,
                  '--showcase-stage': project.stageColor,
                  '--showcase-ink': project.inkColor,
                  '--showcase-faded-ink': project.fadedInk,
                  '--showcase-detail-muted': project.detailMuted,
                  '--showcase-detail': project.detailColor,
                  '--showcase-link-rule': project.linkRule,
                } as ShowcaseStyle}
              >
                <div className="showcase-section__mobile-media">
                  {project.mediaType === 'video' ? (
                    <video
                      className="showcase-section__media"
                      src={project.media}
                      poster={project.fallbackImage}
                      autoPlay
                      loop
                      muted
                      playsInline
                      aria-label={project.mediaAlt}
                    />
                  ) : (
                    <img
                      className="showcase-section__media"
                      src={project.media}
                      alt={project.mediaAlt}
                      decoding="async"
                    />
                  )}
                </div>
                <div className="showcase-section__mobile-copy">
                  <h3>
                    <Link
                      className="showcase-section__mobile-title-link"
                      to={project.projectLink}
                      aria-label={project.title}
                    >
                      <span aria-hidden="true">{project.displayTitle}</span>
                    </Link>
                  </h3>
                  <p>{project.description}</p>
                  {project.techLabel ? <span>{project.techLabel}</span> : null}
                  <Link className="showcase-section__project-link" to={project.projectLink}>
                    {project.projectLinkLabel}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div
          className="showcase-section__edge-preview"
          data-active-project={isPreviewVisible ? activeProject?.id : 'none'}
          data-preview-visible={isPreviewVisible ? 'true' : 'false'}
          aria-live="polite"
        >
          {showcaseProjects.map((project, index) => {
            const isActive = isPreviewVisible && index === activeProjectIndex;

            return (
              <Link
                key={project.id}
                className={`showcase-section__preview-link${
                  isActive ? ' is-active' : ''
                }`}
                data-project-id={project.id}
                to={project.projectLink}
                aria-label={`View ${project.title}`}
                aria-hidden={!isActive}
                tabIndex={isActive ? 0 : -1}
              >
                <figure className="showcase-section__preview">
                  {project.mediaType === 'video' ? (
                    <video
                      className="showcase-section__media"
                      src={project.media}
                      poster={project.fallbackImage}
                      autoPlay
                      loop
                      muted
                      playsInline
                      aria-label={project.mediaAlt}
                    />
                  ) : (
                    <img
                      className="showcase-section__media"
                      src={project.media}
                      alt={project.mediaAlt}
                      decoding="async"
                    />
                  )}
                </figure>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
